import React, { useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { useWindowSize } from 'react-use';
import cn from 'classnames';

import { feSetStylistId, feSidebarClose, feSidebarOpen } from 'actions/ui.actions';
import NO_IMAGE from 'assets/images/slide-out/user-avatar.svg';
import determineIfDisabledDrag from 'bl/scheduler/determineIfDisabledDrag.bl';
import DndDraggable from 'components/dnd/DndDraggable.component';
import Icon from 'components/generic/Icon.component';
import ResponsiveBox from 'components/generic/ResponsiveBox.component';
import MOBILE_VIEWPORT from 'constants/ui/mobileViewport.const';
import SP from 'constants/ui/sidePanel.const';
import useOnClickOutside from 'hooks/useOnClickOutsideRef.hook';
import useTip from 'hooks/useTip.hook';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectCurrentSalonStylists } from 'selectors/currentSalon.selectors';
import { selectSidebarPanel, selectSidebarProcedureModalMounted } from 'selectors/sidebar.selectors';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import asObject from 'utils/objects/asObject.util';
import dnd from 'utils/react/dnd.util';
import truncateString from 'utils/strings/truncateString.util';

import ScheduleColumnMiniModal from 'scheduler/components/Header/ScheduleColumnMiniModal.component';

import CN from './ScheduleColumnHeader.module.scss';

const ScheduleColumnHeader = ({
  isProcedureModalMounted,
  setStylistWeekView,
  stylist,
  panel,
  feSidebarOpen,
  feSidebarClose,
  feSetStylistId,
  stylistList,
  handleProfileImageClick,
}) => {
  const { isDarkMode } = useTheme();
  const salonId = useCurrentSalonId();

  const { val: isModalRendered, tip: toggle, nay: hideModal } = useTip(false);

  const wrapperRef = useRef(null);

  const { id: stylistId, user_name: userName, serves_as_waitlist: isWaitlist, role } = stylist ?? {};
  const displayRoleName = role && role.name;

  const stylistFromRedux = stylistList.find((s) => s.id === stylist?.id);

  const image =
    stylistFromRedux?.profile_image_thumbnail && stylistFromRedux?.profile_image
      ? stylistFromRedux.profile_image_thumbnail
      : stylistFromRedux?.profile_image || NO_IMAGE;

  const handleWeekView = useCallback(() => {
    setStylistWeekView(stylistId);
    feSidebarClose({ tag: 'ScheduleColumnHeader.handleWeekView()' });
  }, [feSidebarClose, setStylistWeekView, stylistId]);

  const openMobileStylistSidebar = useCallback(() => {
    feSetStylistId(asObject(stylist));
    feSidebarOpen({ panel: SP.stylistLog, salonId });
  }, [feSetStylistId, feSidebarOpen, salonId, stylist]);

  useOnClickOutside(wrapperRef, hideModal);

  const { width } = useWindowSize();
  const isMobileViewport = width <= MOBILE_VIEWPORT;

  const contentClasses = cn({
    [CN.columnHeader]: true,
    [CN.isWaitlist]: isWaitlist,
    [CN.active]: isModalRendered,
  });

  return (
    <DndDraggable
      tag="ScheduleColumnHeader--dnd-source"
      key={stylistId}
      draggableId={dnd.dragStylistColumn({ stylist })}
      index={0}
      isDragDisabled={determineIfDisabledDrag({ panel, modal: isProcedureModalMounted })}
    >
      {({ dragHandleProps, draggableProps, innerRef }, snapshot) => {
        const contentStyle = {
          transform: snapshot.isDragging ? 'rotate(-10deg)' : '',
          borderColor: snapshot.isDragging && '#821bb8',
        };

        return (
          <div
            data-bem="ScheduleColumnHeader--el-source"
            ref={(node) => {
              wrapperRef.current = node;
              innerRef(node);
            }}
            {...draggableProps}
            {...dragHandleProps}
            className={cn({
              [CN.component]: true,
              [CN.dark]: isDarkMode,
            })}
          >
            <div
              data-bem="ScheduleColumnHeader__content"
              onClick={isMobileViewport ? openMobileStylistSidebar : toggle}
              role="button"
              tabIndex={0}
              className={contentClasses}
              style={contentStyle}
            >
              <div className={CN.flexRow}>
                <div className={CN.dragIcon}>
                  <Icon variant="drag" variantInvert={isWaitlist} width="md" />
                </div>
              </div>

              <div className={CN.stylistContainer}>
                <div className={CN.profileImgContainer}>
                  <img
                    src={image}
                    alt={userName || 'Stylist image'}
                    className={cn({
                      [CN.avatar]: true,
                      [CN.empty]: image === NO_IMAGE,
                    })}
                  />
                </div>

                <div className={CN.text}>
                  <div
                    className={cn({
                      [CN.stylistName]: true,
                      [CN.smallSize]: userName?.length >= 24,
                    })}
                  >
                    <span className="test">{truncateString({ value: userName ?? '', max: 20, suffix: '...' })}</span>
                  </div>
                  <div className={CN.stylistRole}>
                    {truncateString({ value: displayRoleName ?? '', max: 24, suffix: '...' })}
                  </div>
                </div>
              </div>

              {isModalRendered ? (
                <>
                  <div className={CN.arrowContainer} role="button" tabIndex={0}></div>
                  <div className={CN.icon} />
                </>
              ) : (
                <div
                  className={cn({
                    [CN.arrowContainer]: true,
                    [CN.isWaitlist]: isWaitlist,
                    [CN.dark]: isDarkMode,
                  })}
                />
              )}
            </div>

            {isModalRendered && (
              <ResponsiveBox.Desktop>
                <ScheduleColumnMiniModal
                  stylistId={stylistId}
                  onClickWeekView={handleWeekView}
                  onClickProfileImage={handleProfileImageClick}
                  isMobileViewport={isMobileViewport}
                />
              </ResponsiveBox.Desktop>
            )}
          </div>
        );
      }}
    </DndDraggable>
  );
};

const ScheduleColumnHeaderHoc = connect(
  (state) => ({
    isProcedureModalMounted: selectSidebarProcedureModalMounted(state),
    panel: selectSidebarPanel(state),
    stylists: selectCurrentSalonStylists(state),
    selectedStylistId: state?.scheduler?.stylistId,
    stylistList: state.salon.stylistList.response,
  }),
  mapActionsToDispatchToProps({ feSidebarOpen, feSetStylistId, feSidebarClose }),
)(ScheduleColumnHeader);

export default ScheduleColumnHeaderHoc;
